import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PanoramaFishEyeIcon from "@mui/icons-material/PanoramaFishEye";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC, ReactElement } from "react";
import { INK, STATUS } from "../../theme/tokens";

export enum SelfSchedulingStatus {
	Missing = "Missing",
	Done = "Done",
	Optional = "Optional",
	OverLimit = "Over Limit",
}

const statusToColor = {
	[SelfSchedulingStatus.Missing]: STATUS.ERROR.main,
	[SelfSchedulingStatus.Done]: STATUS.SUCCESS.main,
	[SelfSchedulingStatus.Optional]: alpha(INK.SECONDARY, 0.5),
	[SelfSchedulingStatus.OverLimit]: STATUS.ERROR.main,
} as const satisfies Record<SelfSchedulingStatus, string>;

const smallIconSize = "14px";

const statusToIcon = {
	[SelfSchedulingStatus.Missing]: <CloseIcon sx={{ color: statusToColor.Missing, fontSize: smallIconSize }} />,
	[SelfSchedulingStatus.Done]: <CheckIcon sx={{ color: statusToColor.Done, fontSize: smallIconSize }} />,
	[SelfSchedulingStatus.Optional]: <PanoramaFishEyeIcon sx={{ color: statusToColor.Optional, fontSize: smallIconSize }} />,
	[SelfSchedulingStatus.OverLimit]: <CloseIcon sx={{ color: statusToColor["Over Limit"], fontSize: smallIconSize }} />,
} as const satisfies Record<SelfSchedulingStatus, ReactElement>;

type Props = {
	currentAmount: number;
	maxAmount: number;
	status?: SelfSchedulingStatus;
	label?: string;
};

const gap = "4px";
const width = "86px";
const height = "fit-content";

/**
 * Component to display the current and maximum amount of a resource
 * @param currentAmount
 * @param maxAmount
 * @param label
 * @param status - status of the resource, can be Missing, Done, or Optional
 */
const AmountsAndStatus: FC<Props> = ({ currentAmount, maxAmount, label, status = SelfSchedulingStatus.Missing }) => {
	return (
		<Box sx={{ width, height, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap }}>
			<Typography sx={{ color: INK.SECONDARY, fontWeight: 600, fontSize: "18px", lineHeight: "100%" }}>
				{maxAmount > 0 ? `${currentAmount.toFixed(0)}/${maxAmount.toFixed(0)}` : currentAmount.toFixed(0)}
			</Typography>
			<Typography sx={{ color: alpha(INK.SECONDARY, 0.5), fontWeight: 500, fontSize: "14px", lineHeight: "100%" }}>{label}</Typography>
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				{statusToIcon[status]}
				<Typography sx={{ ml: "2px", color: statusToColor[status], fontWeight: 600, fontSize: smallIconSize, lineHeight: "100%" }}>{status}</Typography>
			</Box>
		</Box>
	);
};

export default AmountsAndStatus;
